import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, Input, Typography } from '../../../Material-UI/Components';
import APIService from '../../../service/APIService';
import { useLoader, useAppContext } from '../../../store/context';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import AuthShell from '../AuthShell';

const useStyles = makeStyles((theme) => ({
  signUpForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  fieldWrap: {
    marginTop: 8,
  },
  input: {
    width: '100%',
    margin: '10px 0 !important',
    background: '#ffffff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 18,
      background: '#f8fbff',
      minHeight: 56,
    },
  },
  helperText: {
    marginTop: 4,
    marginLeft: 12,
    fontSize: 12,
    color: '#64748b',
  },
  strengthWrap: {
    margin: '6px 12px 0',
  },
  agreement: {
    marginTop: 16,
    fontSize: 14,
    lineHeight: 1.75,
    color: '#475569',
  },
  agreementLink: {
    color: '#0369a1',
    fontWeight: 700,
  },
  captchaWrap: {
    marginTop: 18,
    padding: '16px',
    borderRadius: 18,
    border: '1px solid rgba(14, 165, 233, 0.18)',
    background: '#f8fbff',
    overflowX: 'auto',
    [theme.breakpoints.down('xs')]: {
      padding: '12px',
      '& > div': {
        transform: 'scale(0.92)',
        transformOrigin: 'left top',
      },
    },
  },
  primaryAction: {
    marginTop: '18px !important',
    minHeight: 52,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
    boxShadow: '0 16px 28px rgba(14, 165, 233, 0.28)',
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: '0.08em',
    '&:hover': {
      background: 'linear-gradient(135deg, #075985 0%, #0284c7 100%)',
    },
  },
  noteCard: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid rgba(14, 165, 233, 0.18)',
    background: '#f0f9ff',
    padding: '14px 16px',
  },
  noteLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0ea5e9',
  },
  noteText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.6,
    color: '#334155',
  },
  sectionLabel: {
    marginTop: 18,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#94a3b8',
  },
  error: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid #fecaca',
    background: '#fff1f2',
    padding: '12px 14px',
    color: '#b91c1c',
    fontSize: 14,
    lineHeight: 1.6,
  },
}));

const SITE_KEY = '6LfQo_4oAAAAAIk0HA8sWAOzGrcUKtVA81YzWI5d';
const DEV_MODE =
  process.env.REACT_APP_DEV_MODE === 'true' ||
  process.env.REACT_APP_DISABLE_RECAPTCHA === 'true' ||
  isPrivateDevHost();

const SignUp = () => {
  const history = useHistory();
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const showLoader = useLoader();
  const { state } = useAppContext();
  const { email: signedMail } = state.user;
  const [recaptchaToken, setRecaptchaToken] = React.useState(null);
  const redirectPath = getSafeRedirectPath();

  if (signedMail) {
    window.location = redirectPath || '/track/summary';
  }

  const validateForm = () => {
    if (password !== confirmPassword) {
      setError('Password and confirm password do not match.');
      return false;
    }
    if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
      setError('Enter a valid email.');
      return false;
    }

    return true;
  };

  const handleRecaptchaChange = React.useCallback((value) => {
    setRecaptchaToken(value);
    setError('');
  }, []);

  const handleSubmit = (evt) => {
    evt.preventDefault();
    if (!DEV_MODE && !recaptchaToken) {
      setError('Please complete the reCAPTCHA check.');
      return;
    }
    if (validateForm()) {
      showLoader(true);
      APIService.signUp(name, email, password)
        .then(() => { window.location = redirectPath || '/track/summary'; })
        .catch((data) => {
          const dres = data.response;
          if (dres && dres.data && dres.data.message) {
            setError(`Error occurred ${JSON.stringify(dres.data.message)}`);
          } else {
            setError('Error occurred. Please check the inputs and try again.');
          }
        })
        .finally(() => showLoader(false));
    }
  };

  return (
    <AuthShell
      pageTitle="Sign Up | MyJobAide"
      canonicalPath="/track/signup"
      robotsContent="noindex,follow"
      eyebrow="Career Operating System"
      heroTitle="Create your MyJobAide workspace."
      heroSubtitle="Start with a modern career command center that helps you understand your direction, close the right gaps, and act with better timing."
      heroBadges={[
        {
          label: 'Unify signals',
          value: 'Profile, readiness, strategy',
          hint: 'Bring your current experience and target direction into one interpreted system.',
        },
        {
          label: 'Act with clarity',
          value: 'What changed and what next',
          hint: 'Track meaningful progress instead of juggling disconnected job-search tools.',
        },
      ]}
      heroQuoteTitle="What you create here"
      heroQuoteBody="Your account becomes the home for targeting, execution, interview preparation, and momentum tracking, all grounded in one shared career truth."
      formEyebrow="Create account"
      formTitle="Set up your MyJobAide ID"
      formSubtitle="Create your account to start building your personalized career operating system."
      footerPrompt="Already have an account?"
      footerActionLabel="Sign in here"
      onFooterAction={() => history.push(redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login')}
    >
      <form className={classes.signUpForm} onSubmit={handleSubmit}>
        <div className={classes.fieldWrap}>
          <Input
            required
            className={classes.input}
            type="text"
            label="Name"
            onChange={(e) => setName(e.target.value)}
            value={name}
            fullWidth
          />
        </div>
        <div className={classes.fieldWrap}>
          <Input
            required
            className={classes.input}
            type="text"
            label="Email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            fullWidth
          />
          <div className={classes.helperText}>Verification mail will be sent.</div>
        </div>
        <div className={classes.fieldWrap}>
          <Input
            required
            className={classes.input}
            type="password"
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            fullWidth
          />
        </div>
        <div className={classes.strengthWrap}>
          <PasswordStrengthMeter password={password} />
        </div>
        <div className={classes.fieldWrap}>
          <Input
            required
            className={classes.input}
            type="password"
            label="Confirm password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            value={confirmPassword}
            fullWidth
          />
        </div>

        <Typography className={classes.agreement}>
          By signing up, I agree to MyJobAide&apos;s{' '}
          <a className={classes.agreementLink} href="#">Terms</a>,{' '}
          <a className={classes.agreementLink} href="#">Privacy Policy</a>, and{' '}
          <a className={classes.agreementLink} href="#">E-Sign Consent</a>.
        </Typography>

        <div className={classes.noteCard}>
          <div className={classes.noteLabel}>Before you continue</div>
          <div className={classes.noteText}>
            We use your account to keep your progress, recommendations, and next-step guidance connected across the workspace.
          </div>
        </div>

        <div className={classes.sectionLabel}>Verify you are creating a real workspace</div>

        <div className={classes.captchaWrap}>
          {DEV_MODE ? (
            <div className={classes.noteText}>
              Development mode is enabled for this environment, so reCAPTCHA verification is bypassed while you test the signup flow.
            </div>
          ) : (
            <ReCAPTCHA sitekey={SITE_KEY} onChange={handleRecaptchaChange} />
          )}
        </div>

        <Button
          name="Sign up"
          type="submit"
          className={classes.primaryAction}
          fullWidth
          size="large"
        >
          Create workspace
        </Button>

        {error ? <div className={classes.error}>{error}</div> : null}
      </form>
    </AuthShell>
  );
};

export default SignUp;

function isPrivateDevHost() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname || '';
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname.endsWith('.local') ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
  );
}

function getSafeRedirectPath() {
  if (typeof window === 'undefined') {
    return '';
  }

  const searchParams = new URLSearchParams(window.location.search);
  const redirect =
    searchParams.get('redirect') ||
    searchParams.get('redirectafterlogin') ||
    '';
  if (!redirect.startsWith('/') || redirect.startsWith('//')) {
    return '';
  }

  return redirect;
}
